import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useCashbookStatus } from "../../../../helpers/cashbookStatus";
import { enterPrintRows, getPrintCheck, savePrintTotal } from "../../../../services/cashBookApi";
import { useKT } from "../../i18n/kt";
import { kp } from "../../karkhanaPaths";
import { newRequestId } from "../../rounds/entryQueue";
import { fmtDay, rupees } from "../../rates/rateModel";
import PageHeader from "../../ui/PageHeader";
import { confirmDialog } from "../../ui/dialog";
import CashbookGuard, { readRole } from "../CashbookGuard";
import { fmtTime, num } from "../money";
import { PRINT_GEOMETRY } from "../print/CashSheetPrint";
import type { CashRowView, Override, PrintCheck, RowItem } from "../types";
import { doubtText, isOwnerRole, myUserId, ownerRefusal, signedRupees } from "./ownerText";
import PhotoBand, { RowCrop } from "./PhotoBand";
import {
  bandOf,
  correctionsOf,
  Draft,
  gapAmount,
  gapCleared,
  groupRows,
  isOverDue,
  isOwn,
  keyAction,
  latestPhotoOfPage,
  OVER_DUE_REASONS,
  pageRowRange,
  paidToSend,
  rowItem,
  screenOrder,
  sectionsOf,
  verifyBlock,
  VerifyBlock,
} from "./sheetCheckModel";
import "../cashbook.scss";
import "./owner.scss";

/** Where the TOTAL PAID box sits on the last page (below the 15 row slots), for the TOTAL step's photo. */
const TOTAL_BAND = {
  center: (PRINT_GEOMETRY.bodyTopMm + PRINT_GEOMETRY.rowsPerPage * PRINT_GEOMETRY.rowHeightMm + 9) / PRINT_GEOMETRY.pageHeightMm,
  height: 14 / PRINT_GEOMETRY.pageHeightMm,
};

const BLOCK_TEXT: Record<Exclude<VerifyBlock, null | "GAP">, string> = {
  VERIFIED: "Already verified",
  OWN: "Someone else checks this row",
  NOTHING_ENTERED: "Type what the paper says first",
  OVER_DUE_REASON: "More than due: pick why first",
  TWICE_REASON: "Paid twice: say why both stay",
  RANGE: "More than 1.5 times the due. Check the paper.",
};

const cleanMoney = (s: string) => {
  const t = s.replace(/[^\d.]/g, "");
  const [a, ...b] = t.split(".");
  return b.length ? `${a}.${b.join("").slice(0, 2)}` : a;
};

type NetFail = { message: string; retry: () => void } | null;

const CheckScreen = () => {
  const { t } = useKT();
  const { printId } = useParams<{ printId: string }>();
  const status = useCashbookStatus();
  const owner = isOwnerRole(readRole());
  const me = myUserId();
  const [check, setCheck] = useState<PrintCheck | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [total, setTotal] = useState("");
  const [retyping, setRetyping] = useState(false);
  const [totalBusy, setTotalBusy] = useState(false);
  const [totalError, setTotalError] = useState<string | null>(null);
  const [override, setOverride] = useState<null | { kind: "total" } | { kind: "row"; rowId: string }>(null);
  const [reason, setReason] = useState("");
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [rowErr, setRowErr] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [flagged, setFlagged] = useState<Record<string, boolean>>({});
  const [current, setCurrent] = useState<string | null>(null);
  const [viewPage, setViewPage] = useState<number | null>(null);
  const [groupBusy, setGroupBusy] = useState(false);
  const [groupError, setGroupError] = useState<string | null>(null);
  const [netFail, setNetFail] = useState<NetFail>(null);
  const shownAt = useRef<Record<string, number>>({});
  const loadedAt = useRef(Date.now());
  const inputs = useRef<Record<string, HTMLInputElement | null>>({});

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const res = await getPrintCheck(printId);
      if (res?.success && res.data) {
        setCheck(res.data);
        loadedAt.current = Date.now();
      } else setLoadError(ownerRefusal(res, t));
    } catch {
      setLoadError(t("Couldn't load. Check the network."));
    }
  }, [printId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
  }, [load]);

  const sections = useMemo(() => (check ? sectionsOf(check) : { look: [], spot: [], clean: [] }), [check]);
  const order = useMemo(() => screenOrder(sections), [sections]);
  const shaOf = useCallback((r: CashRowView) => (check ? latestPhotoOfPage(check.photos, r.page)?.sha256 || null : null), [check]);
  const group = useMemo(() => (check ? groupRows(check, drafts, shaOf) : []), [check, drafts, shaOf]);

  useEffect(() => {
    if (check && !current && order.length) setCurrent((order.find((r) => r.verifyState !== "VERIFIED") || order[0]).id);
  }, [check, current, order]);
  useEffect(() => {
    if (current && !shownAt.current[current]) shownAt.current[current] = Date.now();
  }, [current]);

  const header = <PageHeader title={check ? `${check.print.sheetNo}${check.print.holder ? ` · ${check.print.holder.name}` : ""}` : t("Sheet check")} back={kp.cashBook()} backLabel={t("Back")} />;

  if (loadError && !check)
    return (
      <div className="cb cbo">
        {header}
        <div className="cb-error" role="alert">
          <span>{loadError}</span>
          <button type="button" className="kh-btn" onClick={load}>
            {t("Retry")}
          </button>
        </div>
      </div>
    );
  if (!check)
    return (
      <div className="cb cbo" role="status" aria-label={t("Loading…")}>
        {header}
        <div className="cb-skeleton is-big" />
        <div className="cb-skeleton" />
        <div className="cb-skeleton" />
      </div>
    );

  const print = check.print;
  const rowsPerPage = print.rowsPerPage || PRINT_GEOMETRY.rowsPerPage;
  const cleared = gapCleared(check);
  const gap = gapAmount(check);
  const currentRow = check.rows.find((r) => r.id === current) || null;
  const needTotal = check.paperTotal === null || check.paperTotal === undefined || retyping;
  const page = needTotal ? print.pages : viewPage ?? currentRow?.page ?? 1;
  const pagePhotos = check.photos.filter((p) => p.kind === "PAY_SHEET" && p.page === page).sort((a, b) => (a.uploadedAt < b.uploadedAt ? 1 : -1));
  const photo = pagePhotos[0] || null;
  const band = needTotal ? TOTAL_BAND : currentRow && currentRow.page === page ? bandOf(currentRow.rowIndex, currentRow.page, rowsPerPage) : null;
  const range = pageRowRange(check.rows, page);
  const superseded = print.state === "SUPERSEDED";
  const verifiedCount = check.rows.filter((r) => r.verifyState === "VERIFIED").length;
  const done = print.checked || (check.rows.length > 0 && verifiedCount === check.rows.length);

  const replaceRows = (rows: CashRowView[]) =>
    setCheck((c) => (c ? { ...c, rows: c.rows.map((r) => rows.find((x) => x.id === r.id) || r) } : c));

  const saveTotal = async (ov: Override) => {
    const paperTotal = ov ? check.paperTotal || total : total;
    if (!paperTotal) return;
    setTotalBusy(true);
    setTotalError(null);
    try {
      const res = await savePrintTotal(printId, { paperTotal, override: ov });
      if (!res?.success || !res.data) {
        setTotalError(ownerRefusal(res, t));
        return;
      }
      setCheck(res.data);
      setRetyping(false);
      setOverride(null);
      setReason("");
      status.refresh();
    } catch {
      setTotalError(t("Not saved. Check the network and retry."));
    } finally {
      setTotalBusy(false);
    }
  };

  /** Send rows (≤ 10). Each item succeeds or fails on its own; a lost answer offers Retry. */
  const send = async (items: RowItem[], ov: Override, isGroup = false): Promise<void> => {
    const ids = items.map((i) => i.rowId);
    const mark = (on: boolean) => setBusy((b) => ({ ...b, ...Object.fromEntries(ids.map((id) => [id, on])) }));
    mark(true);
    setNetFail(null);
    if (isGroup) setGroupError(null);
    setRowErr((e) => {
      const n = { ...e };
      ids.forEach((id) => delete n[id]);
      return n;
    });
    try {
      const res = await enterPrintRows(printId, { batchId: newRequestId(), items, override: ov });
      if (!res?.success || !res.data) {
        const msg = ownerRefusal(res, t);
        if (isGroup) setGroupError(msg);
        else setRowErr((e) => ({ ...e, ...Object.fromEntries(ids.map((id) => [id, msg])) }));
        return;
      }
      const data = res.data;
      replaceRows(data.results.filter((x) => x.row).map((x) => x.row as CashRowView));
      const failed = data.results.filter((x) => !x.ok);
      setRowErr((e) => ({ ...e, ...Object.fromEntries(failed.map((x) => [x.rowId, ownerRefusal(x, t)])) }));
      if (isGroup && failed.length) setGroupError(t("{{n}} not verified. See the rows marked below.", { n: failed.length }));
      setDrafts((d) => {
        const n = { ...d };
        data.results.filter((x) => x.ok).forEach((x) => delete n[x.rowId]);
        return n;
      });
      setCheck((c) =>
        c ? { ...c, gap: data.gap, expectedTotal: data.expectedTotal, print: { ...c.print, checked: data.checked || c.print.checked } } : c
      );
      if (failed.some((x) => x.code === "CHANGED_MEANWHILE" || x.code === "PHOTO_CHANGED" || x.code === "ALREADY_VERIFIED")) load();
      status.refresh();
    } catch {
      setNetFail({ message: t("Not saved. Check the network and retry."), retry: () => send(items, ov, isGroup) });
    } finally {
      mark(false);
    }
  };

  const onScreen = (id: string) => Date.now() - (shownAt.current[id] || loadedAt.current);

  const next = (from: CashRowView) => {
    const i = order.findIndex((r) => r.id === from.id);
    const after = order.slice(i + 1).find((r) => r.verifyState !== "VERIFIED");
    if (after) setCurrent(after.id);
  };

  const enterOnly = (r: CashRowView) => {
    const d = drafts[r.id];
    const paid = paidToSend(r, d);
    if (paid === null) return;
    send([rowItem(r, paid, false, d, null, onScreen(r.id))], null);
  };

  const verifyOne = async (r: CashRowView, ov: Override = null, asDue = false) => {
    const d = asDue && r.netDue !== null ? { ...drafts[r.id], paid: r.netDue } : drafts[r.id];
    const paid = paidToSend(r, d);
    if (paid === null) return;
    const sha = shaOf(r);
    // No photo of this page (office print, page never sent): verify from the paper only after a yes.
    if (!sha && !ov && !(await confirmDialog({ title: t("No photo of this page. Verify from the paper you hold?"), ok: t("Verify") }))) return;
    send([rowItem(r, paid, true, d, sha, onScreen(r.id))], ov).then(() => next(r));
  };

  const verifyGroup = () => {
    if (!group.length) return;
    setGroupBusy(true);
    send(
      group.map((r) => rowItem(r, r.netDue as string, true, undefined, shaOf(r), onScreen(r.id))),
      null,
      true
    ).finally(() => setGroupBusy(false));
  };

  const setDraft = (id: string, patch: Draft) => setDrafts((d) => ({ ...d, [id]: { ...d[id], ...patch } }));

  const onKeys = (e: React.KeyboardEvent) => {
    if (!currentRow || needTotal || done) return;
    const el = e.target as HTMLElement;
    const rowAmount = el?.dataset?.rowAmount !== undefined;
    // A focused button, chip, link or other field handles its own keys: Enter on "Not paid" must never
    // verify the row as paid in full. The laptop keys work on the row and its amount field only.
    if (!rowAmount && /^(BUTTON|A|INPUT|TEXTAREA|SELECT)$/.test(el?.tagName || "")) return;
    const inField = rowAmount;
    const a = keyAction(e.key, inField);
    if (!a) return;
    if (a.kind === "move") {
      e.preventDefault();
      const i = order.findIndex((r) => r.id === currentRow.id);
      const nextRow = order[Math.min(Math.max(i + a.by, 0), order.length - 1)];
      setCurrent(nextRow.id);
      setViewPage(null);
      setTimeout(() => inputs.current[nextRow.id]?.focus(), 0);
    } else if (a.kind === "due") {
      e.preventDefault();
      const d = drafts[currentRow.id];
      const typed = d?.paid !== undefined;
      // Enter = paid as due; refused while the TOTAL gap is open (D11).
      if (!typed && !cleared) return;
      if (verifyBlock(currentRow, typed ? d : { ...d, paid: currentRow.netDue ?? undefined }, check)) {
        if (typed) enterOnly(currentRow);
        return;
      }
      verifyOne(currentRow, null, !typed);
    } else if (a.kind === "flag") {
      e.preventDefault();
      setFlagged((f) => ({ ...f, [currentRow.id]: !f[currentRow.id] }));
      if (drafts[currentRow.id]?.paid !== undefined) enterOnly(currentRow);
      next(currentRow);
    } else if (a.kind === "type") {
      e.preventDefault();
      setDraft(currentRow.id, { paid: (drafts[currentRow.id]?.paid || "") + a.digit });
      inputs.current[currentRow.id]?.focus();
    }
  };

  // ---- done (L4) ------------------------------------------------------------------------------
  if (done && !needTotal) {
    const corrections = correctionsOf(check.rows);
    const holder = print.holder?.name;
    return (
      <div className="cb cbo">
        {header}
        <div className="cb-card is-ok" role="status" aria-live="polite">
          <p>
            <strong>
              {t("{{sheetNo}} checked. {{n}} rows verified, {{c}} corrected.", { sheetNo: print.sheetNo, n: verifiedCount, c: corrections.length })}
            </strong>
          </p>
        </div>
        {corrections.length ? (
          <ul className="cb-list cbo-corrections">
            {corrections.map((c) => (
              <li key={c.rowIndex} className="cb-item">
                <span className="cb-item__main">
                  <span>
                    {c.personId ? <Link to={kp.cashWorker(c.personId)}>{c.name}</Link> : c.name} <s>{rupees(c.due)}</s> → <strong>{rupees(c.paid)}</strong>
                  </span>
                  <span className="cb-item__sub">
                    {[
                      holder ? t("{{name}}'s cash in hand {{delta}}", { name: holder, delta: signedRupees(c.delta) }) : "",
                      c.delta > 0 ? t("{{name}}: {{amount}} more due, goes on the next sheet", { name: c.name, amount: rupees(c.delta) }) : "",
                      c.delta < 0 ? t("{{name}}: {{amount}} more than due", { name: c.name, amount: rupees(-c.delta) }) : "",
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        ) : null}
        <Link to={kp.cashBook()} className="cb-btn is-primary">
          {t("Back to To check")}
        </Link>
      </div>
    );
  }

  // ---- photo panel ------------------------------------------------------------------------------
  const photoPanel = (
    <aside className="cbo-check__photo" aria-label={t("Photo")}>
      <div className="cbo-photo__head">
        {photo ? (
          <span title={photo.sha256}>
            {t("Photo {{time}} · Print {{n}} · not edited", { time: fmtTime(photo.uploadedAt), n: print.revision })}
            {photo.handwritten ? ` · ${t("Handwritten")}` : ""}
            {photo.blurryAccepted ? ` · ${t("Blurry")}` : ""}
          </span>
        ) : (
          <span>{t("Page {{page}} not sent yet", { page })}</span>
        )}
        {print.pages > 1 && !needTotal ? (
          <span className="cbo-pager">
            <button type="button" className="kh-btn" disabled={page <= 1} onClick={() => setViewPage(page - 1)} aria-label={t("Previous page")}>
              ‹
            </button>
            <span>
              {range
                ? t("Page {{page}} of {{pages}} · rows {{from}}–{{to}}", { page, pages: print.pages, from: range[0], to: range[1] })
                : t("Page {{page}} of {{pages}}", { page, pages: print.pages })}
            </span>
            <button type="button" className="kh-btn" disabled={page >= print.pages} onClick={() => setViewPage(page + 1)} aria-label={t("Next page")}>
              ›
            </button>
          </span>
        ) : null}
      </div>
      <PhotoBand photoId={photo?.id || null} band={band} alt={t("Pay sheet {{sheetNo}}, page {{page}}", { sheetNo: print.sheetNo, page })} emptyText={t("Page {{page}} not sent yet", { page })} />
      {pagePhotos.length > 1 ? <p className="cb-muted">{t("{{n}} photos of this page; the newest is shown.", { n: pagePhotos.length })}</p> : null}
    </aside>
  );

  const sub = (
    <p className="cbo-sub">
      {[
        t("Print {{n}}", { n: print.revision }),
        t("{{n}} rows", { n: print.rowCount }),
        `${fmtDay(print.from)} – ${fmtDay(print.to)}`,
        t("due {{amount}}", { amount: rupees(print.totalNetDue) }),
      ].join(" · ")}
    </p>
  );

  // ---- step 1: TOTAL (L2/I2/M2, D12) ------------------------------------------------------------
  if (needTotal) {
    return (
      <div className="cb cbo">
        {header}
        {sub}
        <div className="cbo-check">
          {photoPanel}
          <section className="cbo-check__work">
            <form
              className="cbo-total"
              onSubmit={(e) => {
                e.preventDefault();
                saveTotal(null);
              }}
            >
              <label className="cb-field" htmlFor="cbo-total">
                <span className="cbo-total__q">{t("Type the TOTAL written on the paper")}</span>
                <span className="cb-muted">{t("The TOTAL PAID box on the last page.")}</span>
                <span className="cbo-money">
                  <span aria-hidden="true">₹</span>
                  <input
                    id="cbo-total"
                    inputMode="decimal"
                    autoComplete="off"
                    autoFocus
                    value={total}
                    onChange={(e) => setTotal(cleanMoney(e.target.value))}
                    aria-describedby={totalError ? "cbo-total-err" : undefined}
                  />
                </span>
              </label>
              {totalError ? (
                <p className="cb-error" id="cbo-total-err" role="alert">
                  {totalError}
                </p>
              ) : null}
              <button type="submit" className="cb-btn is-primary" disabled={!total || totalBusy}>
                {totalBusy ? t("Saving…") : t("Next")}
              </button>
              {retyping ? (
                <button type="button" className="cb-link" onClick={() => setRetyping(false)}>
                  {t("Keep {{amount}}", { amount: rupees(check.paperTotal || 0) })}
                </button>
              ) : null}
            </form>
          </section>
        </div>
      </div>
    );
  }

  // ---- step 2: rows (L3) ------------------------------------------------------------------------
  const totalsDiffer = check.declaredTotal !== null && check.paperTotal !== null && num(check.declaredTotal) !== num(check.paperTotal);
  const enteredTotal = rupees(check.paperTotal || 0);

  const renderRow = (r: CashRowView) => {
    const d = drafts[r.id];
    const paid = paidToSend(r, d);
    const sha = shaOf(r);
    const block = verifyBlock(r, d, check);
    const verified = r.verifyState === "VERIFIED";
    const dueKnown = r.netDue !== null;
    const corrected = r.paid !== null && dueKnown && r.enteredAs !== "MATCHES_NET" && num(r.paid) !== num(r.netDue);
    const byWho = r.enteredBy ? (r.enteredBy.userId === me ? t("by you") : t("by {{name}}", { name: r.enteredBy.name })) : "";
    const fieldValue = d?.paid !== undefined ? d.paid : r.paid !== null && r.enteredAs !== "BLANK" ? r.paid : "";
    const blank = paid === "";
    const draftChanged = d?.paid !== undefined && (r.paid === null || (d.paid === "" ? r.enteredAs !== "BLANK" : num(d.paid) !== num(r.paid) || r.enteredAs === "BLANK"));
    const blockTitle = block === "GAP" ? t("Explain {{gap}} first", { gap: rupees(gap) }) : block ? t(BLOCK_TEXT[block]) : "";
    const chips = r.doubts.filter((x) => x !== "SPOT_CHECK" || !r.blind);
    const isCurrent = r.id === current;
    return (
      <li
        key={r.id}
        className={`cbo-row${isCurrent ? " is-current" : ""}${verified ? " is-done" : ""}`}
        onClick={() => {
          if (!isCurrent) {
            setCurrent(r.id);
            setViewPage(null);
          }
        }}
      >
        <div className="cbo-row__head">
          <span className="cbo-row__n">#{r.rowIndex}</span>
          <strong className="cbo-row__name">
            {r.personId ? (
              <Link to={kp.cashWorker(r.personId)} title={t("Worker payments")} onClick={(e) => e.stopPropagation()}>
                {r.name}
              </Link>
            ) : (
              r.name
            )}
          </strong>
          <span className="cbo-row__code">{r.code}</span>
          <span className="cbo-row__due">{dueKnown ? t("Due {{amount}}", { amount: rupees(r.netDue) }) : t("Type what the paper says")}</span>
        </div>

        {chips.length || flagged[r.id] ? (
          <div className="cbo-row__chips">
            {chips.map((x) => (
              <span key={x} className={`cbo-chip${x === "RECORDED_BY_YOU" || x === "YOUR_OWN_PAY" ? " is-own" : " is-doubt"}`}>
                {doubtText(x, t, { n: r.editCount, first: r.firstPaid })}
              </span>
            ))}
            {flagged[r.id] ? <span className="cbo-chip is-flag">{t("Flagged")}</span> : null}
          </div>
        ) : null}
        {!sha && !verified ? (
          <div className="cbo-row__chips">
            <span className="cbo-chip is-doubt">{t("No photo of this page yet")}</span>
          </div>
        ) : null}

        {verified ? (
          <div className="cbo-row__done">
            <span className="cbo-row__amt">
              {corrected ? <s>{rupees(r.netDue)}</s> : null} <strong>{r.enteredAs === "BLANK" ? t("Not paid") : rupees(r.paid)}</strong>
            </span>
            <span className="cb-state is-ok">
              {r.selfVerified ? t("Verified by {{name}} (self-verified)", { name: r.verifiedBy?.name || "" }) : t("Verified by {{name}}", { name: r.verifiedBy?.name || "" })}
            </span>
          </div>
        ) : (
          <>
            {corrected && !draftChanged ? (
              <div className="cbo-row__corrected">
                <s>{rupees(r.netDue)}</s> <strong>{r.enteredAs === "BLANK" ? t("Not paid") : rupees(r.paid)}</strong>
                {byWho ? ` · ${byWho}` : ""}
              </div>
            ) : null}
            <div className="cbo-row__entry">
              <label className="cbo-money is-row">
                <span className="cbo-sr">{t("Paid to {{name}}", { name: r.name })}</span>
                <span aria-hidden="true">₹</span>
                <input
                  ref={(el) => {
                    inputs.current[r.id] = el;
                  }}
                  data-row-amount=""
                  inputMode="decimal"
                  autoComplete="off"
                  value={fieldValue || ""}
                  disabled={superseded || busy[r.id]}
                  onFocus={() => {
                    setCurrent(r.id);
                    setViewPage(null);
                  }}
                  onChange={(e) => setDraft(r.id, { paid: cleanMoney(e.target.value) })}
                />
              </label>
              <button type="button" className={`cb-chip${blank ? " is-on" : ""}`} aria-pressed={blank} disabled={superseded || busy[r.id]} onClick={() => setDraft(r.id, { paid: "" })}>
                {t("Not paid")}
              </button>
            </div>

            {isOverDue(paid, r.netDue) ? (
              <div className="cbo-row__reason">
                <span className="cb-muted">{t("More than due: why?")}</span>
                <div className="cb-chips">
                  {OVER_DUE_REASONS.map((o) => {
                    const on = (d?.overDueReason ?? r.overDueReason) === o.code;
                    return (
                      <button key={o.code} type="button" className={`cb-chip${on ? " is-on" : ""}`} aria-pressed={on} onClick={() => setDraft(r.id, { overDueReason: o.code })}>
                        {t(o.label)}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : null}

            {r.doubts.includes("PAID_TWICE") ? (
              <label className="cb-field">
                <span className="cb-field__label">{t("Keep both: why?")}</span>
                <input value={d?.twiceReason ?? r.twiceReason ?? ""} onChange={(e) => setDraft(r.id, { twiceReason: e.target.value })} placeholder={t("e.g. two different weeks")} />
              </label>
            ) : null}

            {isOwn(r) ? (
              <p className="cbo-row__own">{t(r.canVerify === "YOUR_OWN_PAY" ? "Your own pay. Someone else checks it." : "You recorded this. Someone else checks it.")}</p>
            ) : null}

            <div className="cbo-row__actions">
              {draftChanged ? (
                <button type="button" className="kh-btn" disabled={busy[r.id] || superseded} onClick={() => enterOnly(r)}>
                  {t("Save")}
                </button>
              ) : null}
              {isOwn(r) ? (
                owner ? (
                  <button
                    type="button"
                    className="kh-btn"
                    disabled={busy[r.id] || superseded || paid === null}
                    title={paid === null ? t(BLOCK_TEXT.NOTHING_ENTERED) : undefined}
                    onClick={() => {
                      setReason("");
                      setOverride({ kind: "row", rowId: r.id });
                    }}
                  >
                    {t("Verify anyway")}
                  </button>
                ) : null
              ) : (
                <button
                  type="button"
                  className="kh-btn is-primary"
                  disabled={!!block || busy[r.id] || superseded}
                  title={blockTitle || undefined}
                  aria-describedby={block && block !== "OWN" ? `gap-${r.id}` : undefined}
                  onClick={(e) => {
                    e.stopPropagation();
                    verifyOne(r);
                  }}
                >
                  {busy[r.id] ? t("Saving…") : t("Verify")}
                </button>
              )}
              {block === "GAP" || (block && block !== "OWN" && block !== "VERIFIED" && isCurrent) ? (
                <span className="cb-muted" id={`gap-${r.id}`}>
                  {blockTitle}
                </span>
              ) : null}
            </div>
            {rowErr[r.id] ? (
              <p className="cbo-row__err" role="alert">
                {rowErr[r.id]}
              </p>
            ) : null}
          </>
        )}
      </li>
    );
  };

  const section = (title: string, rows: CashRowView[], extra?: React.ReactNode) =>
    rows.length ? (
      <section className="cbo-section">
        <h3 className="cb-section">{title}</h3>
        {extra}
        <ul className="cbo-rows">{rows.map(renderRow)}</ul>
      </section>
    ) : null;

  const overrideRow = override?.kind === "row" ? check.rows.find((r) => r.id === override.rowId) || null : null;

  return (
    <div className="cb cbo">
      {header}
      {sub}
      {superseded ? (
        <div className="cb-card is-warn" role="status">
          <p>{t("This print was replaced by a newer print. Rows can't be checked here.")}</p>
        </div>
      ) : null}
      <div className="cbo-check">
        {photoPanel}
        <section className="cbo-check__work" onKeyDown={onKeys}>
          <div className="cbo-totals" aria-live="polite">
            <p className="cbo-totals__line">
              {t("Paper TOTAL {{amount}}", { amount: enteredTotal })}
              {check.declaredTotal !== null ? ` · ${t("Supervisor wrote {{amount}}", { amount: rupees(check.declaredTotal) })}` : ""}
              {totalsDiffer ? <span className="cbo-chip is-doubt">{t("Totals differ")}</span> : null}{" "}
              <button type="button" className="cb-link" onClick={() => { setTotal(check.paperTotal || ""); setRetyping(true); }}>
                {t("Change")}
              </button>
            </p>
            {check.totalOverridden ? (
              <div className="cb-card is-warn">
                <p>{t("You said the paper's sum is wrong. Check each row against the photo.")}</p>
              </div>
            ) : cleared ? (
              <div className="cb-card is-ok">
                <p>{t("Total matches: {{paid}} paid of {{due}} due.", { paid: enteredTotal, due: rupees(print.totalNetDue) })}</p>
              </div>
            ) : (
              <div className="cb-card is-bad" role="alert">
                <p>
                  {t(
                    "{{gap}} unexplained. Due minus the rows you changed is {{expected}}, but the paper says {{paper}}. Find the rows that differ before verifying.",
                    { gap: rupees(gap), expected: rupees(check.expectedTotal), paper: enteredTotal }
                  )}
                </p>
                <button type="button" className="cb-link" onClick={() => { setReason(""); setOverride({ kind: "total" }); }}>
                  {t("The paper's sum is wrong")}
                </button>
              </div>
            )}
          </div>

          {netFail ? (
            <div className="cb-error" role="alert">
              <span>{netFail.message}</span>
              <button type="button" className="kh-btn" onClick={netFail.retry}>
                {t("Retry")}
              </button>
            </div>
          ) : null}

          {section(t("Needs a look ({{n}})", { n: sections.look.length }), sections.look)}
          {section(
            t("Spot checks ({{n}})", { n: sections.spot.length }),
            sections.spot,
            <p className="cb-muted">{t("{{n}} rows picked at random to check one at a time.", { n: sections.spot.length })}</p>
          )}
          {section(
            t("Not changed in the app ({{n}})", { n: sections.clean.length }),
            sections.clean,
            <div className="cbo-group">
              {group.length ? (
                <div className="cbo-strip" aria-label={t("Photo of each row")}>
                  {group.map((r) => (
                    <figure key={r.id} className="cbo-strip__item">
                      <RowCrop photoId={latestPhotoOfPage(check.photos, r.page)?.id || null} band={bandOf(r.rowIndex, r.page, rowsPerPage)} label={t("Row {{n}}, {{name}}", { n: r.rowIndex, name: r.name })} />
                      <figcaption>
                        #{r.rowIndex} {r.name} · {rupees(r.netDue)}
                      </figcaption>
                    </figure>
                  ))}
                </div>
              ) : null}
              <button
                type="button"
                className="cb-btn is-primary"
                disabled={!group.length || groupBusy || superseded}
                title={!cleared ? t("Explain {{gap}} first", { gap: rupees(gap) }) : undefined}
                onClick={verifyGroup}
              >
                {groupBusy ? t("Saving…") : t("Verify these {{n}}", { n: group.length })}
              </button>
              {!cleared ? <p className="cb-muted">{t("Explain {{gap}} first", { gap: rupees(gap) })}</p> : null}
              {groupError ? (
                <p className="cbo-row__err" role="alert">
                  {groupError}
                </p>
              ) : null}
            </div>
          )}
          <p className="cb-muted cbo-keys">{t("Laptop keys: Enter = paid as due · ↑ ↓ move · F = flag · digits type the amount.")}</p>
        </section>
      </div>

      {override ? (
        <div className="cbo-dialog" role="dialog" aria-modal="true" aria-labelledby="cbo-dialog-title">
          <div className="cbo-dialog__box">
            <h3 id="cbo-dialog-title">
              {override.kind === "total" ? t("The paper's sum is wrong") : t('Verify your own entry? It stays marked "self-verified" in the exceptions report.')}
            </h3>
            {override.kind === "total" ? <p className="cb-muted">{t("The gap stops blocking. Every row still needs its own check against the photo.")}</p> : null}
            <label className="cb-field">
              <span className="cb-field__label">{t("Why?")}</span>
              <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} autoFocus />
            </label>
            {totalError && override.kind === "total" ? <p className="cb-error">{totalError}</p> : null}
            <div className="cbo-dialog__btns">
              <button type="button" className="kh-btn" onClick={() => setOverride(null)}>
                {override.kind === "total" ? t("Back") : t("Leave for someone else")}
              </button>
              <button
                type="button"
                className="kh-btn is-primary"
                disabled={!reason.trim() || totalBusy}
                onClick={() => {
                  if (override.kind === "total") saveTotal({ reason: reason.trim() });
                  else if (overrideRow) {
                    verifyOne(overrideRow, { reason: reason.trim() });
                    setOverride(null);
                  }
                }}
              >
                {override.kind === "total" ? t("Save reason") : t("Verify anyway")}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

/** /karkhana/money/cash-book/check/:printId — owners and managers only (V1_SPEC 11.6 L2-L4). */
const SheetCheck = () => (
  <CashbookGuard staffOnly>
    <CheckScreen />
  </CashbookGuard>
);

export default SheetCheck;
